import {
  getCategoriesToDisplay,
  sortOptionsByCategory,
} from '@/storybook/components/FormCollection/utils/categoryOptions';
import { createStableObjectHash } from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const getValueKey = (valueToCheck: unknown) =>
  valueToCheck !== null && typeof valueToCheck === 'object'
    ? createStableObjectHash(valueToCheck)
    : String(valueToCheck);

type UseMultiSelectOptionsProps<T> = {
  options?: Partial<SelectItem<T>>[];
  unfilteredOptions?: Partial<SelectItem<T>>[];
  value: Array<T>;
  memoizedValueList: Array<T>;
  searchInput?: string;
};

type KeyedSelectItem<T> = SelectItem<T> & { __internalKey: string };

export const useMultiSelectOptionsFloatingLabel = <T>({
  options,
  unfilteredOptions,
  value,
  memoizedValueList,
  searchInput,
}: UseMultiSelectOptionsProps<T>) => {
  const { t } = useTranslation();

  const allOptions = useMemo<KeyedSelectItem<T>[]>(() => {
    const sourceOptions = options || unfilteredOptions || [];

    const validOptions = sourceOptions
      .filter((optionItem): optionItem is SelectItem<T> => optionItem.value != null && optionItem.label != null)
      .map(optionItem => ({
        ...optionItem,
        __internalKey: getValueKey(optionItem.value),
      }));

    return sortOptionsByCategory(validOptions, t('components.dropdownPanel.noCategory'));
  }, [options, unfilteredOptions, t]);

  const selectedKeysSet = useMemo(() => new Set(value.map(valueItem => getValueKey(valueItem))), [value]);

  const isSelected = useCallback(
    (valueToCheck: T) => selectedKeysSet.has(getValueKey(valueToCheck)),
    [selectedKeysSet],
  );

  const searchResults = useMemo<KeyedSelectItem<T>[]>(() => {
    if (!searchInput) return allOptions;
    const lowerCaseSearchInput = searchInput.toLowerCase();

    return allOptions.filter(
      optionItem =>
        String(optionItem.label).toLowerCase().includes(lowerCaseSearchInput) ||
        String(optionItem.category || '')
          .toLowerCase()
          .includes(lowerCaseSearchInput),
    );
  }, [allOptions, searchInput]);

  const { selectedOptions, notSelectedOptions } = useMemo(() => {
    const seenSelectedKeys = new Set<string>();
    const seenNotSelectedKeys = new Set<string>();

    // Use memoizedValueList for initial grouping when dropdown opens
    const memoizedSelectedKeysSet = new Set(memoizedValueList.map(valueItem => getValueKey(valueItem)));

    const grouped = searchResults.reduce<{
      selectedOptions: SelectItem<T>[];
      notSelectedOptions: SelectItem<T>[];
    }>(
      (accumulator, optionItem) => {
        const deduplicationKey = optionItem.category
          ? `${optionItem.__internalKey}-${optionItem.category}`
          : optionItem.__internalKey;

        const isOptionSelected = memoizedSelectedKeysSet.has(optionItem.__internalKey);
        const targetArray = isOptionSelected ? 'selectedOptions' : 'notSelectedOptions';
        const seenKeys = isOptionSelected ? seenSelectedKeys : seenNotSelectedKeys;

        const isDuplicate = seenKeys.has(deduplicationKey);
        if (!isDuplicate) {
          seenKeys.add(deduplicationKey);
          accumulator[targetArray].push(optionItem);
        }

        return accumulator;
      },
      { selectedOptions: [], notSelectedOptions: [] },
    );

    // Push orphaned (disabled) selections to the bottom of the selected group so
    // still-applicable choices stay at the top.
    grouped.selectedOptions.sort(
      (optionA, optionB) => Number(optionA.disabled ?? false) - Number(optionB.disabled ?? false),
    );

    return grouped;
  }, [searchResults, memoizedValueList]);

  const categoriesToDisplay = useMemo(() => getCategoriesToDisplay(notSelectedOptions), [notSelectedOptions]);

  const selectedOptionValues = useMemo(() => selectedOptions.map(optionItem => optionItem.value), [selectedOptions]);

  const notSelectedOptionValues = useMemo(
    () => notSelectedOptions.map(optionItem => optionItem.value),
    [notSelectedOptions],
  );

  return {
    allOptions,
    isSelected,
    searchResults,
    selectedOptions,
    notSelectedOptions,
    categoriesToDisplay,
    selectedOptionValues,
    notSelectedOptionValues,
  };
};
